import type { BiHotel, BiProperty, BiPropertyGroup, BiScope, OptionsBasis, PropertyGroup, RevenueScope, RevenueValues, VatBasis } from '@app/model/bi/Bi';
import type { MonthRange, MonthRangePreset } from '@ui/selector/MonthRangePicker';

// The pure functions of the BI pages (no React, no router): the hooks in BiScopeHooks.ts use them.

export const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// DD/MM/YYYY (Settings RQ-07).
export const formatDate = (isoDate: string): string => `${isoDate.slice(8, 10)}/${isoDate.slice(5, 7)}/${isoDate.slice(0, 4)}`;

// DD/MM/YYYY HH:MM of an ISO date-time that the server sends in the hotel time zone (with its offset): no conversion here.
export const formatDateTime = (isoDateTime: string): string => `${formatDate(isoDateTime.slice(0, 10))} ${isoDateTime.slice(11, 16)}`;

export const shortName = (property: BiProperty, companyName: string): string => property.name.replace(`, ${companyName}`, '');

// "1 property", "18 properties".
export const propertyCount = (n: number): string => `${n} ${n === 1 ? 'property' : 'properties'}`;

// The import time and the stay dates of a set of properties: the oldest import, the first and the last stay date.
export const scopeValues = (properties: BiHotel[]): Pick<BiScope, 'importedAt' | 'firstStay' | 'lastStay'> => {
	const values = (pick: (p: BiHotel) => string | null) => properties.map(pick).filter((v): v is string => v !== null);
	const imports = values(p => p.importedAt).sort((a, b) => Date.parse(a) - Date.parse(b));
	const firsts = values(p => p.firstStay).sort();
	const lasts = values(p => p.lastStay).sort();
	return { importedAt: imports[0] ?? null, firstStay: firsts[0] ?? null, lastStay: lasts[lasts.length - 1] ?? null };
};

// "2026-10" plus n months (n can be negative).
export const addMonths = (yearMonth: string, n: number): string => {
	const index = Number(yearMonth.slice(0, 4)) * 12 + Number(yearMonth.slice(5, 7)) - 1 + n;
	return `${Math.floor(index / 12)}-${String(index % 12 + 1).padStart(2, '0')}`;
};

// The longest period of the page (the limit of findSegmentationReport and findSegmentEvolution), and the longest period
// that the Week view shows (decision Q2: about 53 bars).
export const MAX_MONTHS = 24;
export const WEEK_MAX_MONTHS = 12;

// The number of months of a period, both ends included ("2026-04" – "2027-09" = 18).
export const monthsIn = (range: MonthRange): number =>
	(Number(range.to.slice(0, 4)) - Number(range.from.slice(0, 4))) * 12 + Number(range.to.slice(5, 7)) - Number(range.from.slice(5, 7)) + 1;

// The period presets of the page (decision Q4), from the month of today, cut to the imported months. A preset with no
// imported month drops out; two presets with the same months keep the first.
export const periodPresets = (asOfMonth: string, min: string, max: string): MonthRangePreset[] => {
	const raw: MonthRangePreset[] = [
		{ id: 'this-month', label: 'This month', range: { from: asOfMonth, to: asOfMonth } },
		{ id: 'next-3-months', label: 'Next 3 months', range: { from: asOfMonth, to: addMonths(asOfMonth, 2) } },
		{ id: 'next-6-months', label: 'Next 6 months', range: { from: asOfMonth, to: addMonths(asOfMonth, 5) } },
		{ id: 'next-12-months', label: 'Next 12 months', range: { from: asOfMonth, to: addMonths(asOfMonth, 11) } },
		{ id: 'last-3-months', label: 'Last 3 months', range: { from: addMonths(asOfMonth, -2), to: asOfMonth } },
		{ id: 'last-6-months', label: 'Last 6 months', range: { from: addMonths(asOfMonth, -5), to: asOfMonth } },
		{ id: 'last-12-months', label: 'Last 12 months', range: { from: addMonths(asOfMonth, -11), to: asOfMonth } },
		{ id: 'year-to-date', label: 'Year to date', range: { from: `${asOfMonth.slice(0, 4)}-01`, to: asOfMonth } },
		{ id: 'this-year', label: 'This year', range: { from: `${asOfMonth.slice(0, 4)}-01`, to: `${asOfMonth.slice(0, 4)}-12` } },
		{ id: 'last-year', label: 'Last year', range: { from: `${Number(asOfMonth.slice(0, 4)) - 1}-01`, to: `${Number(asOfMonth.slice(0, 4)) - 1}-12` } },
	];
	const out: MonthRangePreset[] = [];
	raw.forEach(preset => {
		const range: MonthRange = { from: preset.range.from < min ? min : preset.range.from, to: preset.range.to > max ? max : preset.range.to };
		if (range.from > range.to) return;
		if (out.some(p => p.range.from === range.from && p.range.to === range.to)) return;
		out.push({ ...preset, range });
	});
	return out;
};

const YEAR_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

// The Property groups that the user can choose: each group with the properties of the BI scope of the user (the server sends
// only those; the account list decides), in alphabetical order; a group with no such property does not show (decision 7).
export const visibleGroups = (scope: BiScope | undefined, groups: PropertyGroup[]): BiPropertyGroup[] => {
	const properties = scope?.properties ?? [];
	return groups
		.map(g => ({ id: g.id, title: g.title, properties: properties.filter(p => g.companyHotelIds.includes(p.companyHotelId)) }))
		.filter(g => g.properties.length > 0)
		.sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }));
};

// The address holds the scope (?hotel=all | a company hotel id, or ?hotel=all&group=<id> for a Property group) and the stay
// months of the page, for the chart and the table (?from=YYYY-MM&to=YYYY-MM), grill decision 14: a change of hotel keeps
// the report and the period.
// Each value is checked: a hotel that the account does not hold (a stale link, another account) or no hotel gives the hotel
// that the app has open (appHotelId) when it is a BI property, else the first property with reservations (BI hotel picker
// decision 2); a period outside the imported months is cut to them (a reversed one is turned round), and a period over
// MAX_MONTHS keeps its first MAX_MONTHS months. No period: "This year", cut to the imported months (decision Q1). An old
// link with ?year=YYYY (the year tabs before the one picker) opens Jan–Dec of that year. The revenue switches (ROO2-236
// D1): ?revenue=total and ?vat=net; any other value is the default, Room only and Incl. VAT. A Property group (?group=<id>
// with ?hotel=all) that the user cannot see, or that an admin deleted, gives All properties (grill default).
export const resolveBiAddress = (scope: BiScope | undefined, params: URLSearchParams, appHotelId: number | null = null, groups: BiPropertyGroup[] = []) => {
	const properties = scope?.properties ?? [];
	const hotelParam = params.get('hotel');
	const groupParam = params.get('group');
	const propertyGroup = hotelParam === 'all' && groupParam !== null ? (groups.find(g => String(g.id) === groupParam) ?? null) : null;
	const property = hotelParam === 'all' ? null
		: (properties.find(p => String(p.companyHotelId) === hotelParam)
			?? properties.find(p => p.companyHotelId === appHotelId)
			?? properties.find(p => p.reservationsSource !== null) ?? null);
	const companyHotelId = property?.companyHotelId ?? null;
	const asOfMonth = scope?.asOf.slice(0, 7) ?? '';
	// The values of the selected scope: one property, the properties of a group, or all properties.
	const values = property ?? (propertyGroup === null ? scope : scopeValues(propertyGroup.properties));
	const first = values?.firstStay?.slice(0, 7) ?? asOfMonth;
	const last = values?.lastStay?.slice(0, 7) ?? asOfMonth;
	const clamp = (m: string) => (m < first ? first : (m > last ? last : m));
	const rawFrom = params.get('from');
	const rawTo = params.get('to') ?? rawFrom;
	const valid = rawFrom !== null && rawTo !== null && YEAR_MONTH.test(rawFrom) && YEAR_MONTH.test(rawTo);
	const legacyYear = params.get('year');
	const thisYear = asOfMonth.slice(0, 4);
	const [lo, hi] = valid ? [rawFrom, rawTo].sort()
		: (legacyYear !== null && /^\d{4}$/.test(legacyYear) ? [`${legacyYear}-01`, `${legacyYear}-12`] : [`${thisYear}-01`, `${thisYear}-12`]);
	const from = clamp(lo);
	const to = clamp(hi);
	const period: MonthRange = { from, to: monthsIn({ from, to }) > MAX_MONTHS ? addMonths(from, MAX_MONTHS - 1) : to };
	const revenueScope: RevenueScope = params.get('revenue') === 'total' ? 'TOTAL' : 'ROOM';
	const vat: VatBasis = params.get('vat') === 'net' ? 'NET' : 'INCL';
	// ROO2-310 B8, AC 7: the Options switch, ?options=include; any other value (or none) is the default, Exclude.
	const options: OptionsBasis = params.get('options') === 'include' ? 'INCLUDE' : 'EXCLUDE';
	// The properties of the selection, for the empty states, the sources and the tables by property.
	const selected = property !== null ? [property] : (propertyGroup?.properties ?? properties);
	return {
		companyHotelId, property, propertyGroup, propertyGroupId: propertyGroup?.id ?? null, properties: selected,
		period, asOfMonth, first, last, revenueScope, vat, options,
		importedAt: values?.importedAt ?? null, firstStay: values?.firstStay ?? null, lastStay: values?.lastStay ?? null,
	};
};

// The name of the Excel file of a BI report: "<report> <property> <stay months> <as of>.xlsx", for example
// "Segmentation Condominio Monti Boutique Hotel Jan-Dec 2026 2026-10-21.xlsx" or "Room types All properties Nov 2026-Feb
// 2027 2026-10-21.xlsx". null = all properties. A character that a file name cannot hold becomes a space.
export const exportFileName = (propertyName: string | null, range: MonthRange, asOf: string, report = 'Segmentation'): string =>
	`${report} ${fileNamePart(propertyName ?? 'All properties')} ${fileMonths(range)} ${asOf}.xlsx`;

// The name of an Excel file of the Segment evolution card (ROO2-280 D14): "<report> <property> <file> <stay months> <as of>.xlsx",
// for example "Segment evolution All properties Current view Room nights by month Jan-Dec 2026 2026-10-24.xlsx" or
// "Segment evolution Hotel Sonya All data Jan-Dec 2026 2026-10-24.xlsx". The report comes first, as the other BI exports.
// ROO2-285: the Channels files take the same form ("Channels All properties Current view table Jan-Dec 2026 2026-10-24.xlsx").
export const evolutionFileName = (propertyName: string | null, file: string, range: MonthRange, asOf: string, report = 'Segment evolution'): string =>
	`${report} ${fileNamePart(propertyName ?? 'All properties')} ${fileNamePart(file)} ${fileMonths(range)} ${asOf}.xlsx`;

// ROO2-288 B11: the name of an Excel file of the Daily Performance report, as the server names it: "Daily performance
// <property> <file> <months of the window> <as of>.xlsx", for example "Daily performance Hotel Sonya Current view Oct 2026 2026-10-14.xlsx".
export const dailyFileName = (propertyName: string, file: string, range: MonthRange, asOf: string): string =>
	`Daily performance ${fileNamePart(propertyName)} ${fileNamePart(file)} ${fileMonths(range)} ${asOf}.xlsx`;

// The stay months of a file name: "Jan-Dec 2026", "Nov 2026-Feb 2027", "Mar 2026".
const fileMonths = (range: MonthRange): string => {
	const month = (yearMonth: string) => `${MONTH_LABELS[Number(yearMonth.slice(5, 7)) - 1]} ${yearMonth.slice(0, 4)}`;
	if (range.from === range.to) return month(range.from);
	return range.from.slice(0, 4) === range.to.slice(0, 4)
		? `${MONTH_LABELS[Number(range.from.slice(5, 7)) - 1]}-${month(range.to)}`
		: `${month(range.from)}-${month(range.to)}`;
};

// A part of a file name: a character that a file name cannot hold becomes a space.
const fileNamePart = (text: string): string => text.replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim();

// ROO2-280 decision 23: a money value to the cent as the Excel exports round it (BigDecimal.valueOf(x).setScale(2, HALF_UP)):
// the shortest decimal text of the number, rounded half away from zero. Math.round(x * 100) / 100 gives 1 for 1.005.
export const roundCents = (value: number | null): number | null => {
	if (value === null) return null;
	const parts = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]\d+))?$/.exec(String(value));
	if (parts === null) return value;
	const [, sign, whole, fraction = '', exponent = '0'] = parts;
	// The digits and the place of the decimal point, with the exponent applied.
	let digits = whole + fraction;
	let point = whole.length + Number(exponent);
	if (point < 0) {
		digits = '0'.repeat(-point) + digits;
		point = 0;
	}
	digits = digits.padEnd(point + 3, '0');
	const kept = digits.slice(0, point + 2);
	let cents = BigInt(kept === '' ? '0' : kept);
	if (Number(digits[point + 2]) >= 5) cents += BigInt(1);
	const text = cents.toString().padStart(3, '0');
	const rounded = Number(`${text.slice(0, -2)}.${text.slice(-2)}`);
	return rounded === 0 ? 0 : (sign === '-' ? -rounded : rounded);
};

// The revenue of a figure for the switches of the page (ROO2-236). null = no value: a night of the figure has no value of
// it (no room only value yet, or a room type with no VAT rate); the screen shows "–".
export const revenueOf = (values: RevenueValues, scope: RevenueScope, vat: VatBasis): number | null => {
	if (scope === 'TOTAL') return vat === 'NET' ? values.totalRevenueNet : values.totalRevenue;
	return vat === 'NET' ? values.roomRevenueNet : values.roomRevenue;
};

// ROO2-302 (Q1 C): the reports that do not count the held rooms of group blocks yet say so when a property of the scope has them.
export const HELD_BLOCKS_NOTE = 'The held rooms of group blocks are not in this report yet; Daily Performance includes them.';
export const heldBlocksNote = (properties: { heldBlocks?: boolean }[]): string | null =>
	properties.some(p => p.heldBlocks === true) ? HELD_BLOCKS_NOTE : null;

// ROO2-315 part c1: the occupied rooms of a figure by the report rules, the divisor of every ADR; the room nights when the
// report gives none (no rules, or a report that does not ask them yet).
export const occupiedOf = (figure: { roomNights: number, occupiedRooms?: number }): number => figure.occupiedRooms ?? figure.roomNights;

// The name of the selected revenue, for the captions: "room only revenue incl. VAT".
export const revenueName = (scope: RevenueScope, vat: VatBasis): string =>
	`${scope === 'TOTAL' ? 'total revenue' : 'room only revenue'} ${vat === 'NET' ? 'excl.' : 'incl.'} VAT`;

// What the selected revenue holds, for the table caption.
export const revenueContent = (scope: RevenueScope): string =>
	scope === 'TOTAL' ? 'the room and the meals of the rate, without extras' : 'the night price without the meals and the extras';

// Why a figure of the selected revenue has no value, for the notes of the captions: no room only value (a night imported
// before its meals were known) or no value without VAT (a room type with no VAT rate). A figure with no room only value
// has no room only value without VAT either: it names the first reason only.
export type MissingRevenue = { roomOnly: boolean, vatRate: boolean };

export const missingOf = (figures: RevenueValues[], scope: RevenueScope, vat: VatBasis): MissingRevenue => {
	const missing = figures.filter(f => revenueOf(f, scope, vat) === null);
	const roomOnly = scope === 'ROOM' && missing.some(f => f.roomRevenue === null);
	return { roomOnly, vatRate: vat === 'NET' && missing.some(f => scope === 'TOTAL' || f.roomRevenue !== null) };
};

// The note under a table or a chart with a value missing (ROO2-236 decision 5, R2a): `sign` is how the screen shows it,
// `of` what holds the night ("the figure", "the period"). null when every figure has a value.
export const missingNote = (missing: MissingRevenue, sign: string, of: string): string | null => {
	const reasons = [
		missing.roomOnly ? `a night of ${of} has no room only value yet (its meals are not known)` : null,
		missing.vatRate ? `a night of ${of} is on a room type with no VAT rate in the PMS` : null,
	].filter((r): r is string => r !== null);
	if (reasons.length === 0) return null;
	const complete = missing.roomOnly ? '' : ' The values incl. VAT are complete.';
	return `${sign} = no value: ${reasons.join(', or ')}.${complete}`;
};

// U7: "Previous year" is not in the Columns menu. The hint names the year before today; it says that the import holds no
// stay of that year only when the first stay is later (asOfMonth and firstStayMonth are YYYY-MM).
export const previousYearHint = (asOfMonth: string, firstStayMonth: string): string => {
	const previous = Number(asOfMonth.slice(0, 4)) - 1;
	return firstStayMonth > `${previous}-12`
		? `Previous year: the import holds no ${previous} stay yet.`
		: 'Previous year: not available in this version.';
};
